"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { CheckCircle2, Loader2 } from "@/lib/icons"
import { ReviewTagPicker } from "@/components/review-tag-picker"
import { useTranslations } from "next-intl"

interface CollectionReviewTagsProps {
  collectionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmitted?: () => void
}

// Same tap-to-select pattern as CommissionReviewTags, but rating a Goonfolio
// isn't gated by any transaction — anyone logged in who isn't the folio's
// owner can rate it, any time, never required. Can be corrected once (see
// `edited` from the API) before it locks for good.
export function CollectionReviewTags({ collectionId, open, onOpenChange, onSubmitted }: CollectionReviewTagsProps) {
  const t = useTranslations("collectionReviewTags")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [alreadyRated, setAlreadyRated] = useState(false)
  const [locked, setLocked] = useState(false)
  const [rating, setRating] = useState(0)

  useEffect(() => {
    if (!open) return
    setError(null)
    setDone(false)
    setLoadingStatus(true)
    fetch(`/api/collections/${collectionId}/review-tags`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setAlreadyRated(!!data?.rated)
        setLocked(!!data?.edited)
        setSelected(new Set(data?.tags ?? []))
        setRating(Number(data?.rating) || 0)
      })
      .catch(() => {
        setSelected(new Set())
        setRating(0)
      })
      .finally(() => setLoadingStatus(false))
  }, [open, collectionId])

  const toggle = (slug: string) => {
    if (locked) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  async function handleSubmit() {
    if (selected.size === 0) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch(`/api/collections/${collectionId}/review-tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, tags: Array.from(selected) }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || "Failed to submit rating")
      }
      setDone(true)
      onSubmitted?.()
    } catch (err: any) {
      setError(err?.message ?? "Failed to submit rating")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("whatStoodOutAboutThisFolio")}</DialogTitle>
        </DialogHeader>

        {loadingStatus ? (
          <div className="py-6 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : done ? (
          <div className="py-6 text-center space-y-2">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <p className="text-sm text-muted-foreground">{t("thanksYourRatingHelpsThisCreatorEarnBad")}</p>
          </div>
        ) : locked ? (
          <div className="py-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">{t("youVeAlreadyRatedThisFolioAndUsedYourOn")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {alreadyRated ? t("youCanAdjustYourRatingOneTime") : t("tapEverythingThatApplied")}
            </p>
            <ReviewTagPicker
              targetType="goonfolio"
              rating={rating}
              onRatingChange={setRating}
              selectedTags={selected}
              onToggleTag={toggle}
              disabled={locked}
            />

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                {t("cancel")}
              </Button>
              <Button onClick={handleSubmit} disabled={rating === 0 || selected.size === 0 || submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {alreadyRated ? t("updateRatingLastEdit") : t("submitRating")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
